/*eslint-disable max-lines*/
import { reset } from '@amplitude/analytics-browser';
import { useAuthUtils, useUser } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { Menu, Transition } from '@headlessui/react';
import {
  ArrowRightOnRectangleIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Fragment, useState } from 'react';

interface Props {
  collapsed?: boolean;
}

export default function ProfilePopover({ collapsed }: Props) {
  const user = useUser();
  const { profilePic, logout } = useAuthUtils();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = () => {
    setIsLoggingOut(true);
    reset();
    logout({ returnTo: window.location.origin });
  };

  return (
    <div
      className={clsx(collapsed ? 'mb-6' : 'mb-6 ml-2 self-start', 'relative')}
    >
      <Menu as="div" className="relative">
        {({ open, close }) => {
          const isOpen = open || isHovered;

          return (
            <div
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Menu.Button
                className="flex items-center space-x-2 pr-2"
                title="Profile"
              >
                <img
                  className={clsx(
                    'h-8 w-8 rounded-full',
                    collapsed &&
                      'border-onlight-line-2 dark:border-ondark-line-2 mb-1 ml-2 border text-[8px]'
                  )}
                  src={profilePic}
                  alt="user avatar"
                />
                {!collapsed && (
                  <span className="truncate text-sm">{user.name}</span>
                )}
              </Menu.Button>

              <Transition
                as={Fragment}
                show={isOpen}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 -translate-x-2"
                enterTo="transform opacity-100 translate-x-0"
                leave="transition ease-in duration-150"
                leaveFrom="transform opacity-100 translate-x-0"
                leaveTo="transform opacity-0 -translate-x-2"
              >
                <Menu.Items
                  className={clsx(
                    'border-onlight-line-2 dark:border-ondark-line-2 absolute left-full top-1/2 z-[60] ml-1 w-72 -translate-y-[90%] rounded-xl border p-4 drop-shadow-lg',
                    'bg-onlight-bg-1 dark:bg-ondark-bg-1 text-onlight-text-2 dark:text-ondark-text-1'
                  )}
                >
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={profilePic}
                      alt="user avatar"
                      className="my-3 h-24 w-24 rounded-full"
                    />
                    <div className="flex w-full flex-col leading-tight">
                      <span className="break-words text-lg font-medium">
                        {user.name}
                      </span>
                      <span className="text-onlight-text-3 -mt-0.5 break-all text-sm">
                        {user.email}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      block
                      className="text-onlight-text-3 mt-3 flex items-center justify-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLogoutModal(true);
                        close();
                      }}
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      Logout
                    </Button>
                  </div>
                </Menu.Items>
              </Transition>
            </div>
          );
        }}
      </Menu>

      {showLogoutModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={() => setShowLogoutModal(false)}
          />
          <div className="dark:bg-ondark-bg-1 relative z-10 w-full max-w-sm rounded-lg bg-white px-4 py-2 text-center drop-shadow-lg transition-transform duration-200 ease-out">
            <div className="flex items-center justify-between pt-1">
              <h3 className="text-onlight-text-1 dark:text-dark-secondary-text text-lg font-medium">
                Logout
              </h3>
              <Button variant="icon" onClick={() => setShowLogoutModal(false)}>
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <p className="text-onlight-text-2 dark:text-dark-text mt-6 flex items-start gap-1 text-left text-sm">
              <QuestionMarkCircleIcon className="h-5 w-5 pt-0.5" />
              Are you sure you want to log out of your account?
            </p>

            <div className="mt-6 flex justify-end gap-3 pb-2 text-base">
              <Button
                variant="outline"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </Button>

              <Button
                variant="error"
                onClick={handleLogout}
                loading={isLoggingOut}
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
